import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Phone, Mail, Send, MapPin } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) { toast.error('Please fill all fields'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/contact`, form);
      toast.success('Message sent successfully!');
      setForm({ name: '', phone: '', message: '' });
    } catch { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0F172A]">
      <div className="max-w-7xl mx-auto" ref={ref}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7 }} className="md:col-span-5">
            <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4 font-['Outfit']">{t('contact.label')}</p>
            <h2 className="text-3xl sm:text-4xl font-light text-[#F8FAFC] font-['Cormorant_Garamond'] tracking-tight mb-6">{t('contact.title')}</h2>
            <div className="w-16 h-px bg-[#D4AF37] mb-8" />
            <p className="text-[#94A3B8] font-['Outfit'] text-base leading-relaxed mb-10">{t('contact.desc')}</p>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm flex items-center justify-center flex-shrink-0"><Phone className="w-4 h-4 text-[#D4AF37]" /></div>
                <div>
                  <p className="text-xs text-[#94A3B8] font-['Outfit'] uppercase tracking-wider">{t('contact.phone')}</p>
                  <a href="tel:+917780830043" className="text-[#F8FAFC] font-['Outfit'] text-sm hover:text-[#D4AF37] transition-colors">+91 7780830043</a>
                  <span className="text-[#94A3B8] mx-2">/</span>
                  <a href="tel:+917051094852" className="text-[#F8FAFC] font-['Outfit'] text-sm hover:text-[#D4AF37] transition-colors">7051094852</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm flex items-center justify-center flex-shrink-0"><Mail className="w-4 h-4 text-[#D4AF37]" /></div>
                <div>
                  <p className="text-xs text-[#94A3B8] font-['Outfit'] uppercase tracking-wider">{t('contact.email')}</p>
                  <a href="mailto:adnanabass0101@gmail.com" className="text-[#F8FAFC] font-['Outfit'] text-sm hover:text-[#D4AF37] transition-colors">adnanabass0101@gmail.com</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm flex items-center justify-center flex-shrink-0"><MapPin className="w-4 h-4 text-[#D4AF37]" /></div>
                <div>
                  <p className="text-xs text-[#94A3B8] font-['Outfit'] uppercase tracking-wider">{t('contact.address')}</p>
                  <p className="text-[#F8FAFC] font-['Outfit'] text-sm">Seer Hamdan, Anantnag, J&K</p>
                </div>
              </div>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7, delay: 0.2 }} className="md:col-span-7">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-[#0B1120] border border-white/5 rounded-lg p-8">
              <div className="space-y-5">
                <div>
                  <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-bold mb-2 block font-['Outfit']">{t('contact.yourName')}</Label>
                  <Input data-testid="contact-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder={t('contact.namePlaceholder')} className="bg-[#1E293B] border-white/10 text-[#F8FAFC] h-11 placeholder:text-[#475569] focus:ring-[#D4AF37] focus:border-[#D4AF37]" required />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-bold mb-2 block font-['Outfit']">{t('contact.phone')}</Label>
                  <Input data-testid="contact-phone-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder={t('contact.phonePlaceholder')} className="bg-[#1E293B] border-white/10 text-[#F8FAFC] h-11 placeholder:text-[#475569] focus:ring-[#D4AF37] focus:border-[#D4AF37]" required />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-bold mb-2 block font-['Outfit']">{t('contact.label')}</Label>
                  <Textarea data-testid="contact-message-input" value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder={t('contact.messagePlaceholder')} className="bg-[#1E293B] border-white/10 text-[#F8FAFC] placeholder:text-[#475569] focus:ring-[#D4AF37] focus:border-[#D4AF37]" rows={5} required />
                </div>
                <Button type="submit" data-testid="contact-submit-btn" disabled={sending} className="w-full bg-[#D4AF37] text-[#020617] font-semibold hover:bg-[#FCE69C] h-12 transition-all duration-300 font-['Outfit']">
                  {sending ? t('contact.sending') : <><Send className="w-4 h-4 mr-2" /> {t('contact.sendMessage')}</>}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
